import {
    Condition,
    ConditionType,
    PredefinedFilter
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import {
    IReportColumnFormatterArgs,
    IReportHierarchy,
    IReportRowDef,
    IReportRowFormatterArgs
} from "@components/smart/smartTable";
import { IRowValues } from "@components/table";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import { getFiscalYearByDate } from "@pages/fiscalYear/FiscalYear.utils";
import { AccountAnalysisProps } from "@pages/reports/accountAnalysis/AccountAnalysisDef";
import { CUSTOM_DATE_RANGE_ID } from "@pages/reports/customFilterComponents/ComposedDateRange.utils";
import {
    getLabelParameterDrilldownFilters,
    resolveDateRangeCustomValue,
    transformLabelsHierarchyFilter
} from "@pages/reports/expensesAndRevenue/ExpensesAndRevenue.utils";
import {
    ExpensesAndRevenueProps
} from "@pages/reports/expensesAndRevenue/ExpensesAndRevenueDef";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { getCompanyCurrency, isNonProfitCompany } from "@utils/CompanyUtils";
import dayjs from "dayjs";
import i18next from "i18next";

import { BasicInputSizes, FieldType, ReportTableRowType, Sort } from "../../../enums";
import { TRecordAny, TRecordValue, TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { ROUTE_ACCOUNT_ANALYSIS, ROUTE_ACCOUNTING_JOURNAL } from "../../../routes";
import { formatCurrency } from "../../../types/Currency";
import { getUtcDate } from "../../../types/Date";
import {
    CommonReportProps,
    getReportLevelOfDetailDef
} from "../CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback, getComposedDateRangeFieldsDefs,
} from "../customFilterComponents/ComposedDateRange";
import {
    getAccountsParamDef,
    getLabelDrilldownFilters,
    IReportTableDefinition,
    NumberAggregationFunction,
    ReportColumnType,
    ReportConfigGroup,
    TGetReportConfigListItemOverride
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";

export const GeneralLedgerProps = {
    showOffBalanceAccounts: BindingContext.localContext("ShowOffBalanceAccounts"),
    showClosingAccounts: BindingContext.localContext("ShowClosingAccounts"),
    levelOfDetail: BindingContext.localContext("LevelOfDetail"),
    labels: BindingContext.localContext("Labels"),
    showAccountsWithoutRevenue: BindingContext.localContext("ShowAccountsWithoutRevenue"),
    splitIntoPeriods: BindingContext.localContext("SplitIntoPeriods"),
    accounts: BindingContext.localContext("Accounts"),
    balanceDisplay: BindingContext.localContext("BalanceDisplay"),
};

export enum SplitIntoPeriods {
    None = "None",
    Months = "Months",
    Periods = "Periods"
}

export enum BalanceDisplay {
    Full = "Full",
    Simplified = "Simplified",
    Saldo = "Saldo"
}

export const getBalanceItems = () => [
    {
        label: i18next.t("Reporting:GeneralLedger.Include"),
        id: true,
    },
    {
        label: i18next.t("Reporting:GeneralLedger.Exclude"),
        id: false,
    },
];

export const getShowHideItems = () => [
    {
        label: i18next.t("Reporting:Common.Show"),
        id: true,
    },
    {
        label: i18next.t("Reporting:Common.Hide"),
        id: false,
    },
];

export const getCommonPlaceholderReportHierarchy = (): IReportHierarchy => {
    return {
        "Aggregate": true,
        "Groups":
            [
                {
                    "ColumnAlias": "Account_Synthetic"
                }
            ],
        "Columns": [],
        "Aggregations":
            [
                {
                    "ColumnAlias": "InitialDebit",
                    "AggregationFunction": NumberAggregationFunction.Sum
                },
                {
                    "ColumnAlias": "InitialCredit",
                    "AggregationFunction": NumberAggregationFunction.Sum
                },
                {
                    "ColumnAlias": "JournalEntry_DebitAmount",
                    "AggregationFunction": NumberAggregationFunction.Sum
                },
                {
                    "ColumnAlias": "JournalEntry_CreditAmount",
                    "AggregationFunction": NumberAggregationFunction.Sum
                },
                {
                    "ColumnAlias": "FinalDebit",
                    "AggregationFunction": NumberAggregationFunction.Sum
                },
                {
                    "ColumnAlias": "FinalCredit",
                    "AggregationFunction": NumberAggregationFunction.Sum
                }
            ]
    };
};

export const getSimplifiedBalanceAggregation = () => {
    const aggregations = [
        {
            ColumnAlias: "InitialSimplified",
            AggregationFunction: NumberAggregationFunction.Sum
        },
        ...getCommonPlaceholderReportHierarchy().Aggregations.slice(2, -2),
        {
            ColumnAlias: "FinalSimplified",
            AggregationFunction: NumberAggregationFunction.Sum
        }
    ];

    return aggregations;
};

export const getSaldoBalanceAggregation = () => {
    const aggregations = [
        {
            ColumnAlias: "InitialSaldo",
            AggregationFunction: NumberAggregationFunction.Sum
        },
        ...getCommonPlaceholderReportHierarchy().Aggregations.slice(2, -2),
        {
            ColumnAlias: "FinalSaldo",
            AggregationFunction: NumberAggregationFunction.Sum
        }
    ];

    return aggregations;
};

export const getFullBalanceAggregation = () => {
    return [...getCommonPlaceholderReportHierarchy().Aggregations];

};

export const getReportHierarchyAggregationByBalance = (balance: BalanceDisplay) => {
    switch (balance) {
        case BalanceDisplay.Simplified:
            return getSimplifiedBalanceAggregation();

        case BalanceDisplay.Saldo:
            return getSaldoBalanceAggregation();

        case BalanceDisplay.Full:
        default:
            return getFullBalanceAggregation();
    }
};


const parseAccountNumber = (fullAccountName: string) => {
    // filter unfortunately doesn't work with whole account name, so we set just it's number to filter
    return fullAccountName?.toString().split(" ")[0];
};

const parseFilters = (storage: ReportStorage, accFilterName: string, rowValues: IRowValues) => {
    const entity = storage.data.entity;
    const filters: TRecordAny = {
        ...getLabelDrilldownFilters(rowValues, ["COUNT"]),
        ...getLabelParameterDrilldownFilters(storage, ExpensesAndRevenueProps.labels),
    };

    const EXCLUDED_ACCOUNT = isNonProfitCompany(storage.context) ? 961 : 701;
    const _appendExcludedAccount = () => {
        const secondaryAcc = accFilterName === "CreditAccount_Number" ? "DebitAccount_Number" :
            accFilterName === "DebitAccount_Number" ? "CreditAccount_Number" : null;

        if (secondaryAcc) {
            filters[BindingContext.localContext(secondaryAcc)] = [{
                id: "Account_Number_excl",
                type: ConditionType.Excluded,
                condition: Condition.Equals,
                filter: PredefinedFilter.Value,
                value: EXCLUDED_ACCOUNT,
            }];
        }
    };

    if (rowValues.Account) {
        filters[BindingContext.localContext(accFilterName)] = [{
            id: "Account_Number",
            type: ConditionType.Included,
            condition: Condition.Equals,
            filter: PredefinedFilter.Value,
            value: parseAccountNumber(rowValues.Account as string),
        }];
        _appendExcludedAccount();
    } else if (rowValues.Account_Synthetic) {
        filters[BindingContext.localContext(accFilterName)] = [{
            id: "Account_Number",
            type: ConditionType.Included,
            condition: Condition.BeginsWith,
            filter: PredefinedFilter.Value,
            value: parseAccountNumber(rowValues.Account_Synthetic as string),
        }];
        _appendExcludedAccount();
    } else if (entity[GeneralLedgerProps.accounts]?.length > 0) {
        filters[BindingContext.localContext(accFilterName)] = entity[GeneralLedgerProps.accounts];
    }

    if (rowValues.Document_NumberOurs) {
        filters[BindingContext.localContext("Document_NumberOurs")] = [rowValues.Document_NumberOurs];
    }

    if (rowValues.JournalEntry_DateAccountingTransaction) {
        filters[BindingContext.localContext("JournalEntry_DateAccountingTransaction")] = rowValues.JournalEntry_DateAccountingTransaction;
    }

    if (rowValues.JournalEntry_DateAccountingTransaction_MONTH) {
        const date = dayjs(rowValues.JournalEntry_DateAccountingTransaction_MONTH as unknown as Date);
        filters[CommonReportProps.dateRange] = CUSTOM_DATE_RANGE_ID;
        filters[CommonReportProps.dateRangeCustomValue] = {
            from: date.startOf('month').format('YYYY-MM-DD'),
            to: date.endOf('month').format('YYYY-MM-DD'),
        };
    } else if (rowValues.FiscalPeriod_Name) {
        const periodString = rowValues.FiscalPeriod_Name as string;
        const splitData = periodString?.split("/");
        const year = splitData[0];
        const date = getUtcDate(year);

        const fy = getFiscalYearByDate(storage.context, date);
        const periods = fy.Periods;
        const period = periods?.find(period => period.Name === periodString);
        if (period) {
            filters[CommonReportProps.dateRange] = CUSTOM_DATE_RANGE_ID;
            filters[CommonReportProps.dateRangeCustomValue] = {
                from: period.DateStart,
                to: period.DateEnd
            };
        }
    } else {
        const dateRangeBc = storage.data.bindingContext.navigate(CommonReportProps.dateRange);
        const dateRange = storage.getValue(dateRangeBc);

        filters[CommonReportProps.dateRange] = dateRange ?? CUSTOM_DATE_RANGE_ID;
        filters[CommonReportProps.dateRangeCustomValue] = resolveDateRangeCustomValue(storage);
    }


    return filters;
};

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.GeneralLedger;
    const title = i18next.t("Reporting:GeneralLedger.Title");
    const path = "GeneralLedger";
    const initialSortBy = [{ id: "Account_Synthetic", sort: Sort.Asc }];
    const parameters = [
        CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue,
        ExpensesAndRevenueProps.levelOfDetail,
        ExpensesAndRevenueProps.labels,
        GeneralLedgerProps.accounts,
        GeneralLedgerProps.showAccountsWithoutRevenue,
        GeneralLedgerProps.showClosingAccounts,
        GeneralLedgerProps.showOffBalanceAccounts,
        GeneralLedgerProps.splitIntoPeriods,
        GeneralLedgerProps.balanceDisplay
    ];

    const rowsDataFactory = (rowsData: IReportRowDef[], args: IReportRowFormatterArgs): IReportRowDef[] => {
        const showAccountsWithoutRevenue = args.storage.data.entity[GeneralLedgerProps.showAccountsWithoutRevenue];

        if (!showAccountsWithoutRevenue) {
            return rowsData;
        }

        return rowsData.map((rowData) => {
            if (rowData.Type !== ReportTableRowType.Group || rowData.Rows?.length > 0) {
                return rowData;
            }

            const values = { ...rowData.Value };

            // add 0kč to all currency columns of the empty group rows
            for (const col of args.columns) {
                if (!values[col.id] && col.type === ReportColumnType.Currency) {
                    values[col.id] = 0;
                }
            }

            return {
                ...rowData,
                Value: values
            };
        });
    };

    const columnOverrides = (columnId: string) => {
        if (columnId === "Document_NumberOurs") {
            return {
                formatter: (val: TValue, args: IFormatOptions) => {
                    const docType = args.entity["JournalEntry_DocumentTypeCode"];
                    const id = args.entity["Document_Id"] ?? args.entity["PaymentDocument_Id"];
                    const route = `${getRouteByDocumentType(docType)}/${id}`;
                    return getTableDrillDownLink(val as string, {
                        route,
                        context: args.storage.context,
                        storage: args.storage
                    });
                }
            };
        }
        if (columnId.startsWith("JournalEntrySimplified")) {
            return {
                formatter: (val: TValue, args: IFormatOptions) => {
                    const filters = parseFilters(args.storage as ReportStorage, "Account_Number", args.entity);
                    const formattedVal = formatCurrency(val, args.entity["JournalEntry_CurrencyCode"] ?? getCompanyCurrency(args.context));

                    filters[AccountAnalysisProps.sumAmounts] = true;

                    return getTableDrillDownLink(formattedVal, {
                        route: ROUTE_ACCOUNT_ANALYSIS,
                        context: args.storage.context,
                        storage: args.storage,
                        filters
                    });
                }
            };
        }
        if (columnId.startsWith("JournalEntry_CreditAmount") || columnId.startsWith("JournalEntry_DebitAmount")) {
            return {
                formatter: (val: TValue, args: IFormatOptions) => {
                    let filters: TRecordValue;

                    let hasJournalEntries: boolean;
                    if (columnId.startsWith("JournalEntry_CreditAmount")) {
                        hasJournalEntries = Number(args.entity["JournalEntry_CreditCount_SUM"]) > 0;
                        filters = parseFilters(args.storage as ReportStorage, "CreditAccount_Number", args.entity);
                    } else if (columnId.startsWith("JournalEntry_DebitAmount")) {
                        hasJournalEntries = Number(args.entity["JournalEntry_DebitCount_SUM"]) > 0;
                        filters = parseFilters(args.storage as ReportStorage, "DebitAccount_Number", args.entity);
                    }

                    const formattedVal = formatCurrency(val, args.entity["JournalEntry_CurrencyCode"] ?? getCompanyCurrency(args.context));

                    // turnover might be 0, but we still want to show drilldown link if there are journal entries
                    // behind it, so check the count before deciding to show link or not
                    if (val === 0 && !hasJournalEntries) {
                        return formattedVal;
                    }

                    return getTableDrillDownLink(formattedVal, {
                        route: ROUTE_ACCOUNTING_JOURNAL,
                        context: args.storage.context,
                        storage: args.storage,
                        filters
                    });
                }
            };

        }
        return null;
    };

    const configListItemsOverride: TGetReportConfigListItemOverride = {
        Account_Synthetic: {
            isRequired: true,
            isDisabled: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Groups
        },
        Account: {
            isRequired: true,
            isDisabled: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Groups
        },
        JournalEntry_DateAccountingTransaction: {
            isRequired: true,
            isDisabled: true,
            isCopyOnly: false,
            areItemsReadOnly: true,
            hideFromAvailable: true,
            group: ReportConfigGroup.Groups
        },
        InitialDebit: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        InitialCredit: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        JournalEntry_DebitAmount: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        JournalEntry_CreditAmount: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        FinalDebit: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        FinalCredit: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        InitialSimplified: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        FinalSimplified: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        InitialSaldo: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        },
        FinalSaldo: {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            isCopyOnly: false,
            group: ReportConfigGroup.Aggregations
        }
    };

    const disableAggregateButton = true;

    const hierarchy = getCommonPlaceholderReportHierarchy();
    const defaultReportHierarchy = {
        ...hierarchy,
        Groups: [...hierarchy.Groups, { ColumnAlias: "Account" }]
    };
    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue,
                GeneralLedgerProps.accounts,
                GeneralLedgerProps.levelOfDetail,
                GeneralLedgerProps.showAccountsWithoutRevenue,
                GeneralLedgerProps.splitIntoPeriods,
                GeneralLedgerProps.showClosingAccounts,
                GeneralLedgerProps.showOffBalanceAccounts,
                GeneralLedgerProps.labels,
                GeneralLedgerProps.balanceDisplay
            ],
            filterDefinition: {
                ...getComposedDateRangeFieldsDefs(tableId),
                [GeneralLedgerProps.levelOfDetail]: getReportLevelOfDetailDef(),
                [GeneralLedgerProps.accounts]: getAccountsParamDef([], true),
                [GeneralLedgerProps.labels]: {
                    type: FieldType.LabelSelect,
                    label: i18next.t("Reporting:ExpensesAndRevenue.Labels"),
                    filter: {
                        transformFilterValue: transformLabelsHierarchyFilter
                    },
                    fieldSettings: {
                        oneItemPerHierarchy: false,
                        addEmptyItem: false,
                        addEmptyValuesForHierarchies: true,
                        combinedLabelId: true
                    },
                },
                [GeneralLedgerProps.showClosingAccounts]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:GeneralLedger.ShowClosingAccounts"),
                    width: BasicInputSizes.L,
                    defaultValue: false,
                    fieldSettings: {
                        items: getBalanceItems()
                    }
                },
                [GeneralLedgerProps.showOffBalanceAccounts]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:GeneralLedger.ShowOffBalanceAccounts"),
                    width: BasicInputSizes.L,
                    defaultValue: false,
                    fieldSettings: {
                        items: getShowHideItems()
                    }
                },
                [GeneralLedgerProps.showAccountsWithoutRevenue]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:GeneralLedger.ShowWithoutRevenue"),
                    width: BasicInputSizes.L,
                    defaultValue: false,
                    fieldSettings: {
                        items: getShowHideItems()
                    }
                },
                [GeneralLedgerProps.balanceDisplay]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:GeneralLedger.BalanceDisplay"),
                    width: BasicInputSizes.M,
                    defaultValue: BalanceDisplay.Full,
                    fieldSettings: {
                        items: [
                            { 
                                id: BalanceDisplay.Full, 
                                label: i18next.t("Reporting:GeneralLedger.BalanceFull")
                            },
                            {
                                id: BalanceDisplay.Simplified,
                                label: i18next.t("Reporting:GeneralLedger.BalanceSimplified")
                            },
                            {
                                id: BalanceDisplay.Saldo,
                                label: i18next.t("Reporting:GeneralLedger.BalanceSaldo")
                            },
                        ]
                    }
                },
                [GeneralLedgerProps.splitIntoPeriods]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:GeneralLedger.Split"),
                    width: BasicInputSizes.M,
                    defaultValue: SplitIntoPeriods.None,
                    fieldSettings: {
                        items: [
                            { id: SplitIntoPeriods.None, label: i18next.t("Reporting:GeneralLedger.SplitNone") },
                            {
                                id: SplitIntoPeriods.Months,
                                label: i18next.t("Reporting:GeneralLedger.SplitMonths")
                            },
                            {
                                id: SplitIntoPeriods.Periods,
                                label: i18next.t("Reporting:GeneralLedger.SplitPeriods")
                            },
                        ]
                    }
                }
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {}
        }
    ];

    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage, false);
    };

    const columnFormatter = (args: IReportColumnFormatterArgs) => {
        if (args.column?.id !== "Account_Synthetic" && args.columnDef?.Label && args.column) {
            args.column.label = args.columnDef.Label;
        }

        return { ...args.column };
    };


    return {
        title, initialSortBy, id: tableId, path,
        rowsDataFactory, configListItemsOverride, onBeforeLoad,
        columnFormatter,
        disableAggregateButton, defaultReportHierarchy,
        filterBarDef, parameters, columnOverrides
    };
};

setReportDef(ReportId.GeneralLedger, getDefinition);